import React, { useState } from "react";
import Icon from "../../../components/AppIcon";

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const handleContactAction = (type) => {
    switch (type) {
      case "chat":
        // For now, redirect to email with live chat request
        window.location.href =
          "mailto:support@takbusiness.com?subject=Live Chat Request&body=Hi, I would like to start a live chat session. Please respond with your preferred chat platform or schedule a call.";
        break;
      case "email":
        window.location.href =
          "mailto:support@takbusiness.com?subject=Support Request&body=Hello, I need help with TAK Business Manager. Please describe your issue:";
        break;
      case "phone":
        window.location.href = "tel:+254 111462289";
        break;
      default:
        break;
    }
  };

  const faqs = [
    {
      id: 0,
      category: "Getting Started",
      question: "How do I download and set up the TAK Business Manager app?",
      answer: `Getting started is simple and takes just a few minutes:\n\n• Download from App Store or Google Play Store\n• Create your account with email or phone number\n• Set up your business profile (2 minutes)\n• Start using the app immediately\n\nThe app includes a quick tutorial to help you get familiar with all the features.`,
    },
    {
      id: 1,
      category: "Compatibility",
      question: "Which devices and operating systems are supported?",
      answer: `TAK Business Manager works on:\n\n• iOS 12.0 and later (iPhone & iPad)\n• Android 6.0 and later\n• Optimized for both phones and tablets\n• Works on most modern smartphones\n\nThe app automatically adapts to your device screen size for the best experience.`,
    },
    {
      id: 2,
      category: "Offline Use",
      question: "Can I use the app without an internet connection?",
      answer: `Yes! The app works completely offline:\n\n• Process sales and payments\n• Update inventory\n• View reports and analytics\n• Add new products and customers\n• All data syncs automatically when you're back online\n\nYou'll never miss a sale due to poor connectivity.`,
    },
    {
      id: 3,
      category: "Data & Security",
      question: "How secure is my business data on the mobile app?",
      answer: `Your data security is our top priority:\n\n• Bank-level 256-bit encryption\n• Secure cloud backup\n• Biometric authentication support\n• Regular security updates\n• GDPR compliant\n• No data sharing with third parties\n\nYour business information stays private and secure.`,
    },
    {
      id: 4,
      category: "Features",
      question: "What can I do with the mobile app?",
      answer: `The app includes all essential business management features:\n\n• Point of sale (POS) system\n• Inventory management\n• Sales reporting and analytics\n• Customer management\n• Staff management\n• Multi-location support\n• Receipt printing via Bluetooth\n• Real-time notifications\n\nEverything you need to run your business from your phone.`,
    },
    {
      id: 5,
      category: "Support",
      question: "What support is available for the mobile app?",
      answer: `We provide comprehensive support:\n\n• In-app help and tutorials\n• Email support (24/7)\n• Live chat support\n• Video tutorials\n• FAQ section\n• User community forum\n• Regular app updates\n\nOur support team typically responds within 2 hours.`,
    },
    {
      id: 6,
      category: "Pricing",
      question: "Is the app free? Are there any hidden costs?",
      answer: `The app download is completely free:\n\n• Free to download and install\n• Basic features available at no cost\n• Premium features available with subscription\n• No setup fees or hidden charges\n• Cancel anytime\nTransparent pricing with no surprises.`,
    },
    {
      id: 7,
      category: "Sync & Backup",
      question: "How does data sync work across multiple devices?",
      answer: `Seamless synchronization across all your devices:\n\n• Real-time sync when online\n• Automatic backup to secure cloud\n• Access from multiple phones/tablets\n• No data loss between devices\n• Offline changes sync when connected\n• Instant updates across all devices\n\nYour data is always up-to-date everywhere.`,
    },
  ];

  const categories = [...new Set(faqs.map((faq) => faq.category))];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? -1 : id);
  };

  return (
    <section id="faq" className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
            <Icon name="HelpCircle" size={16} className="mr-2" />
            Frequently Asked Questions
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-text-primary mb-6">
            Got Questions? We Have Answers
          </h2>
          <p className="text-lg text-text-secondary">
            Everything you need to know about TAK Business Manager. Can't find
            what you're looking for? Contact our support team.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories?.map((category) => (
            <button
              key={category}
              className="px-4 py-2 bg-white rounded-full text-sm font-medium text-text-secondary hover:text-primary hover:bg-primary/5 transition-colors border border-gray-200"
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs?.map((faq) => (
            <div
              key={faq?.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(faq?.id)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full mr-3">
                      {faq?.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary pr-4">
                    {faq?.question}
                  </h3>
                </div>
                <div className="flex-shrink-0">
                  <Icon
                    name={openFAQ === faq?.id ? "ChevronUp" : "ChevronDown"}
                    size={20}
                    className={`transition-colors ${
                      openFAQ === faq?.id
                        ? "text-primary"
                        : "text-text-secondary"
                    }`}
                  />
                </div>
              </button>

              {openFAQ === faq?.id && (
                <div className="px-6 pb-6">
                  <div className="pt-4 border-t border-gray-100">
                    <div className="prose prose-sm max-w-none">
                      {faq?.answer?.split("\n")?.map((line, index) => (
                        <p
                          key={index}
                          className="text-text-secondary leading-relaxed mb-2"
                        >
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="MessageSquare" size={24} className="text-primary" />
            </div>

            <h3 className="text-2xl font-bold text-text-primary mb-4">
              Still Have Questions?
            </h3>

            <p className="text-text-secondary mb-8">
              Our support team is here to help you succeed. Get in touch and
              we'll respond within 2 hours.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon
                    name="MessageCircle"
                    size={20}
                    className="text-success"
                  />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">
                  Live Chat
                </h4>
                <p className="text-sm text-text-secondary mb-3">
                  Chat with our support team
                </p>
                <button
                  onClick={() => handleContactAction("chat")}
                  className="text-success font-medium text-sm hover:underline transition-colors"
                >
                  Start Chat
                </button>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Mail" size={20} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">
                  Email Support
                </h4>
                <p className="text-sm text-text-secondary mb-3">
                  Get detailed help via email
                </p>
                <button
                  onClick={() => handleContactAction("email")}
                  className="text-primary font-medium text-sm hover:underline transition-colors"
                >
                  Send Email
                </button>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Phone" size={20} className="text-secondary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">
                  Phone Support
                </h4>
                <p className="text-sm text-text-secondary mb-3">
                  Speak directly with our team
                </p>
                <button
                  onClick={() => handleContactAction("phone")}
                  className="text-secondary font-medium text-sm hover:underline transition-colors"
                >
                  Call Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
